import { ApolloClient, NormalizedCacheObject } from 'apollo-boost'

import { ClientBehaviours } from '../../utils'
import { orders } from '../../../graphql/Query/Customer/Orders.graphql'
import {
  Country,
  Order,
  OrderProduct,
  OrdersFilterInput,
  OrderStatus,
} from '../../../generated/graphql'
import {
  EnterpriseGroupedOrderProducts,
  EnterpriseOrderProduct,
} from '../../../../types/enterpriseTypes'

export interface OrdersVariables {
  filter: OrdersFilterInput
  clickAndCollectEnabled?: Boolean
}
export const getOrderDeliveryDateRange = (orderProducts: OrderProduct[]) => {
  const allDeliveryDatesSorted = orderProducts
    .reduce((acc, product) => {
      if (product?.deliveryDateRange) {
        product.deliveryDateRange?.from &&
          acc.push(new Date(`${product.deliveryDateRange?.from}T00:00:00Z`))
        product.deliveryDateRange?.to &&
          acc.push(new Date(`${product.deliveryDateRange?.to}T00:00:00Z`))
      }
      return acc
    }, [] as Date[])
    .sort((a, b) => a.getTime() - b.getTime())

  if (allDeliveryDatesSorted.length === 0) {
    return null
  }

  return {
    from: allDeliveryDatesSorted[0],
    to: allDeliveryDatesSorted[allDeliveryDatesSorted.length - 1],
    __typename: 'deliveryDateRange',
  }
}

export const getOrderTrackingUrl = (productsUrls) => {
  const trackingUrls = productsUrls.reduce((accumulator, product) => {
    if (product.trackingUrls && product.trackingUrls.length > 0) {
      product.trackingUrls.forEach((trackingUrl) => {
        if (!accumulator[trackingUrl]) {
          accumulator[trackingUrl] = []
        }

        accumulator[trackingUrl].push(product)
      })
    } else {
      if (!accumulator['']) {
        accumulator[''] = []
      }
      accumulator[''].push(product)
    }
    return accumulator
  }, {} as OrderProduct[])

  if (trackingUrls.length === 0) {
    return null
  }

  return trackingUrls
}

export const getProductImage = (product?: OrderProduct): string => {
  if (!product) {
    return ''
  }

  if (
    product?.productVariant?.images &&
    product?.productVariant?.images?.length > 0
  ) {
    return product.productVariant.images[0]?.largeProduct ?? ''
  }

  return product?.productVariant?.product?.images[0]?.largeProduct ?? ''
}

export const getProductTitle = (product?: OrderProduct): string => {
  return product?.productVariant?.title ?? ''
}

const getProductUrl = (product?: OrderProduct): string => {
  return product?.productVariant?.product?.url ?? ''
}

const getDeliveryMethod = (orderProducts: OrderProduct[]) => {
  const deliveryMethods: string[] = orderProducts
    .filter((product) => product.deliveryMethod)
    .map((product) => product.deliveryMethod)

  if (deliveryMethods.length === 0 || deliveryMethods[0] === '') {
    return null
  }

  return deliveryMethods[0]
}

const toEnterpriseProducts = (
  orderProducts: OrderProduct[],
): EnterpriseOrderProduct[] => {
  return orderProducts
    .sort(
      (productA: OrderProduct, productB: OrderProduct) =>
        productA.sku - productB.sku,
    )
    .map((product: OrderProduct, index: Number) => {
      return {
        ...product,
        image: getProductImage(product),
        title: getProductTitle(product),
        url: getProductUrl(product),
        externalIdentifier: product?.productVariant?.externalIdentifier || '',
        deliveryMethod: '',
        fulfilmentMethod: product.fulfilmentMethod ?? null,
        store: product.store
          ? {
              ...product.store,
              __typename: 'Store',
              id: `${index}`,
              urlTag: '',
              phoneNumber: '',
              displayName: product.store?.displayName,
              openingTimes: [],
              longitude: 0,
              latitude: 0,
              nearbyStores: [],
              address: {
                ...product.store?.address,
                __typename: 'Address',
                addressLine1: '',
                addresseeName: '',
                clickAndCollect: false,
                country: Country.Gb,
                postalCode: product.store?.address.postalCode || '',
              },
            }
          : null,
      }
    })
}

const groupOrderProducts = (orderProducts: OrderProduct[]) => {
  const enterpriseOrderProducts = toEnterpriseProducts(orderProducts)

  const groups: EnterpriseGroupedOrderProducts = {
    products: enterpriseOrderProducts,
    placedProducts: [],
    processingProducts: [],
    paymentProblemProducts: [],
    cancelledProducts: [],
    pendingCancellationProducts: [],
    dispatchedProducts: [],
    collectedProducts: [],
    readyToCollectProducts: [],
  }

  enterpriseOrderProducts.forEach((product) => {
    if (product.pendingCancelQuantity && product.pendingCancelQuantity > 0) {
      groups.pendingCancellationProducts.push({
        ...product,
        quantity: product.pendingCancelQuantity,
      })
    }
    switch (product.status) {
      case OrderStatus.ReadyToCollect:
        groups.readyToCollectProducts.push(product)
        break
      case OrderStatus.OrderPlaced:
        groups.placedProducts.push(product)
        break
      case OrderStatus.Processing:
        groups.processingProducts.push(product)
        break
      case OrderStatus.Collected:
        groups.collectedProducts.push(product)
        break
      case OrderStatus.PaymentProblem:
        groups.paymentProblemProducts.push(product)
        break
      case OrderStatus.Cancelled:
        groups.cancelledProducts.push(product)
        break
    }
  })
  // @TODO: REBUILD-6000: Refactor Orders
  // this was done outside the switch since we need to group them by the uniqueness of tracking urls
  // and we need to pass the whole array of dispatched products instead of individual one
  groups.dispatchedProducts = getOrderTrackingUrl(
    enterpriseOrderProducts.filter(
      (item) => item.status === OrderStatus.Dispatched,
    ),
  )

  return groups
}

const getBuylist = (products: OrderProduct[]) => {
  return products
    .map((product) => {
      return product.sku
    })
    .toString()
}

export const Orders = (
  client: ApolloClient<NormalizedCacheObject>,
  { behaviours }: { behaviours: ClientBehaviours },
) => async (_parent, args, _context, _info) => {
  let mutableData: any
  let mutableErrors: any

  try {
    if (behaviours.override) {
      const mockOrders = await import('../../data/Orders').then((mod) => {
        return mod.ORDERS
      })
      if (args?.filter?.orderNumber === '678000123') {
        mutableData = {
          customer: {
            orders: {
              ...mockOrders.customer.orders,
              orders: [mockOrders.customer.orders.orders[1]],
            },
          },
        }
      } else if (args?.filter?.orderNumber === '678000016') {
        mutableData = {
          customer: {
            orders: {
              ...mockOrders.customer.orders,
              orders: [
                {
                  ...mockOrders.customer.orders.orders[10],
                },
              ],
            },
          },
        }
        mutableErrors = {
          message: 'Internal Server Error.',
          locations: [{ line: 4, column: 5 }],
          path: ['customer', 'orders'],
          extensions: { classification: 'DataFetchingException' },
        }
      } else if (args?.filter?.orderNumber) {
        mutableData = {
          customer: {
            orders: {
              ...mockOrders.customer.orders,
              orders: mockOrders.customer.orders.orders.filter(
                (order) => order.orderNumber === args?.filter?.orderNumber,
              ),
            },
          },
          __typename: 'Customer',
        }
      } else {
        mutableData = mockOrders
      }
    } else {
      const { data, errors } = await client.query({
        query: orders,
        variables: args,
        errorPolicy: 'ignore',
      })

      mutableData = data
      mutableErrors = errors
    }

    const hasMissingDetails = Boolean(
      args?.filter?.orderNumber && mutableErrors && mutableData,
    )

    const ordersData = mutableData?.customer?.orders.orders.map(
      (order: Order) => {
        return {
          ...order,
          hasMissingDetails,
          deliveryAddress: order?.deliveryAddress,
          deliveryDateRange: getOrderDeliveryDateRange(order.products),
          deliveryMethod: getDeliveryMethod(order.products),
          ...groupOrderProducts(order.products),
          paymentCard: order?.paymentCard,
          paymentType: order?.paymentType,
          buylist: getBuylist(order.products),
          isReturnable: order.isReturnable?.success || false,
          cancellable: order.products?.some(
            (product) => product.cancellable === true,
          ),
        }
      },
    )

    return {
      errors: mutableErrors,
      orders: ordersData,
      hasMore: mutableData?.customer?.orders?.hasMore,
      total: mutableData?.customer?.orders?.total,
      __typename: 'Order',
    }
  } catch {
    throw Error('Network error in Orders.')
  }
}
